import React, { useEffect } from 'react';

import { appendPendingParamsToRoute } from '@aline/common';
import { EditorFindStateProvider } from 'src/editor/editor-find-state-registry';
import { purgeClosedTabScopedState } from 'src/editor/tab-scoped-registry';
import { makeStyles } from 'tss-react/mui';

import { TabActiveProvider } from './tab-active-context';
import { TabContentRoutes } from './tab-content-routes';
import { useTabContext } from './tab-context';
import { TabRouterProvider } from './tab-router-provider';
import type { Tab } from './tab-types';

const useStyles = makeStyles()(() => ({
  tabContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
    width: '100%',
  },
  tabPane: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
  },
}));

/**
 * Renders open tabs with state preservation.
 *
 * Each tab remains mounted under a stable key. React.Activity hides inactive
 * panes instead of unmounting them, so DOM state, DataGrid scroll, editor state,
 * and WebViewer instances survive tab switches without custom restoration.
 */
export function TabActivityArea(): JSX.Element {
  const { classes } = useStyles();
  const { activeTab, tabs } = useTabContext();

  usePurgeClosedTabState(tabs);

  if (!activeTab) return <></>;

  return (
    <div className={classes.tabContainer}>
      {tabs.map(tab => (
        <TabPane key={tab.id} isActive={tab.id === activeTab.id} tab={tab} />
      ))}
    </div>
  );
}

/**
 * Releases the tab-scoped state of tabs this area no longer renders.
 *
 * Ownership lives here rather than in each pane because panes are hidden, not unmounted:
 * a pane's effect cleanup runs on every tab switch and cannot tell a hide from a close.
 * An empty tab list is skipped — it occurs while the tab model initializes, and purging
 * then would drop state that is still in use.
 */
function usePurgeClosedTabState(tabs: Tab[]): void {
  useEffect(() => {
    if (tabs.length === 0) return;
    purgeClosedTabScopedState(tabs.map(tab => tab.id));
  }, [tabs]);
}

function TabPane({ isActive, tab }: { isActive: boolean; tab: Tab }): JSX.Element {
  const { classes } = useStyles();

  return (
    <React.Activity mode={isActive ? 'visible' : 'hidden'}>
      <TabActiveProvider value={isActive}>
        <EditorFindStateProvider tabId={tab.id}>
          <div className={classes.tabPane} data-tab-id={tab.id}>
            <TabRouterProvider isActive={isActive} tab={tab}>
              <TabContentRoutes
                location={appendPendingParamsToRoute(tab.route, tab.pendingParams)}
              />
            </TabRouterProvider>
          </div>
        </EditorFindStateProvider>
      </TabActiveProvider>
    </React.Activity>
  );
}
